/**
 * Browser input adapter: DOM events -> FlashPlayer.injectMouse / injectKey / injectText.
 *
 * - Pointer events on the canvas (mouse, pen, touch) are mapped to stage coordinates through the
 *   host's `toStage` (CSS scaling, letterboxing and devicePixelRatio handled there); the pointer is
 *   captured while pressed so releases outside the canvas still arrive; leaving the canvas sends
 *   the Flash MOUSE_LEAVE ('leave'); only the primary button is forwarded (Flash Player 10.1 has no
 *   right/middle-button events); the context menu is suppressed.
 * - Wheel: Flash delta (+ = away from the user, 3 per notch on Windows); discrete notches (>= 50
 *   CSS px, lines or pages) give whole multiples of 3, small touchpad deltas are accumulated.
 * - Keyboard: physical key (`KeyboardEvent.code`) -> Flash/Windows virtual key code (layout
 *   independent, like Flash on Windows: the "Q/Й" key is 81 on any layout); charCode from
 *   `KeyboardEvent.key` (Cyrillic and other layouts give their UTF-16 character). Browser defaults
 *   are suppressed for keys the game may use (arrows, space, backspace, tab, letters...) unless
 *   Ctrl/Meta is held (browser shortcuts keep working). Keys still held when the window loses
 *   focus are released.
 * - Touch: a finger is a mouse that moves to the touch point, presses and releases; its hover
 *   (MOUSE_OVER tooltips) lasts `touchHoverMs` after the release before MOUSE_LEAVE (0: at once).
 *   A second tap within the double-click time and TOUCH_SLOP_PX of the first one lands on the
 *   first one's point when the first one pressed an object that takes double clicks
 *   (doubleClickEnabled) and the second one presses that object or nothing else interactive (a
 *   finger never hits the same stage pixel twice: Flash's double click needs presses within 4 px);
 *   a quick tap on a neighbouring control is its own tap. A tap that hardly moved releases where
 *   it pressed.
 * - Touch aids (PORT, a UI made for the mouse on a phone; fingers only, never the mouse or a pen; off by default):
 *   a finger that hits no button presses the nearest one within `touchSlopPx` when no other one is about as near
 *   (touch-assist.ts); a finger held still for `longPressMs` shows the hover only (its release is a 'cancel': no
 *   click), and the hover of a long press or of a tap on something that is not a button (a tooltip) stays until the
 *   next touch; `touchAssistAt` turns the aids off where taps must stay exact (a battle map).
 * - A finger's touch the browser cancels (PORT): Android's WebView cancels a touch whose start waited about a second
 *   for a busy page (a slow device drawing a frame): pointercancel right after its pointerdown, none of its moves, and
 *   at the finger's lift a pointerup of a pointer never pressed. The finger is still down: the player keeps the press
 *   (no release, no click) until that lift, which brings the finger's way (a move there: a drag of the battle camera
 *   moves it) and, after the player's next frame (`afterFrame`), the release as for any touch. A press nothing lifts
 *   ends without a click at the next touch, when the window loses focus or at `detach`.
 * - Text: while a Flash input TextField has focus, a hidden <input> receives DOM focus so that
 *   virtual keyboards, IME composition and paste work; committed text goes through
 *   FlashPlayer.injectText (TEXT_INPUT/CHANGE without key events, like Flash). An IME
 *   composition (a phone keyboard composing a word) is shown in the field while it is typed and
 *   replaced by the committed text. The on-screen keyboard offers "go" for Enter, or what the focused field asks
 *   for (TextField.$enterKeyHint: "next" in a form's first field; enterkeyhint); tapping the focused field again
 *   reopens a keyboard the user dismissed.
 * - Cursor: `updateCursor()` applies FlashPlayer.getCursor() (hand over buttons, I-beam over
 *   text, 'none' after Mouse.hide()).
 */
import { clickableOf, pickAt, snapTouch } from '../player/touch-assist';

/** Flash/Windows virtual key code for a `KeyboardEvent.code`. */
const CODE_TO_VK: Record<string, number> = (() => {
  const m: Record<string, number> = {
    Backspace: 8,
    Tab: 9,
    Enter: 13,
    NumpadEnter: 13,
    ShiftLeft: 16,
    ShiftRight: 16,
    ControlLeft: 17,
    ControlRight: 17,
    AltLeft: 18,
    AltRight: 18,
    Pause: 19,
    CapsLock: 20,
    Escape: 27,
    Space: 32,
    PageUp: 33,
    PageDown: 34,
    End: 35,
    Home: 36,
    ArrowLeft: 37,
    ArrowUp: 38,
    ArrowRight: 39,
    ArrowDown: 40,
    PrintScreen: 44,
    Insert: 45,
    Delete: 46,
    MetaLeft: 15,
    MetaRight: 15,
    ContextMenu: 93,
    NumpadMultiply: 106,
    NumpadAdd: 107,
    NumpadSubtract: 109,
    NumpadDecimal: 110,
    NumpadDivide: 111,
    NumLock: 144,
    ScrollLock: 145,
    Semicolon: 186,
    Equal: 187,
    Comma: 188,
    Minus: 189,
    Period: 190,
    Slash: 191,
    Backquote: 192,
    BracketLeft: 219,
    Backslash: 220,
    BracketRight: 221,
    Quote: 222,
    IntlBackslash: 226,
  };
  for (let i = 0; i < 26; i++) m['Key' + String.fromCharCode(65 + i)] = 65 + i;
  for (let i = 0; i <= 9; i++) {
    m['Digit' + i] = 48 + i;
    m['Numpad' + i] = 96 + i;
  }
  for (let i = 1; i <= 24; i++) m['F' + i] = 111 + i;
  return m;
})();

/** Legacy `keyCode` values that differ between browsers and Windows (Firefox). */
const LEGACY_FIX: Record<number, number> = { 59: 186, 61: 187, 173: 189, 224: 15 };

export interface KeyLike {
  code?: string;
  key?: string;
  keyCode?: number;
}

/** Flash keyCode for a DOM keyboard event (0 when unknown). */
export function flashKeyCode(e: KeyLike): number {
  if (e.code && CODE_TO_VK[e.code] !== undefined) return CODE_TO_VK[e.code];
  const kc = e.keyCode ?? 0;
  if (kc === 229) return 229; // IME processing
  return LEGACY_FIX[kc] ?? kc;
}

const NAMED_CHARS: Record<string, number> = { Enter: 13, Tab: 9, Backspace: 8, Escape: 27, Delete: 127 };

/** Flash charCode for a DOM keyboard event (UTF-16 unit of the produced character, else 0). */
export function flashCharCode(e: KeyLike): number {
  const k = e.key ?? '';
  if (k.length === 1) return k.charCodeAt(0);
  if (NAMED_CHARS[k] !== undefined) return NAMED_CHARS[k];
  return 0;
}

/** Flash MouseEvent.delta for a DOM wheel event (positive = scroll up / away from the user). */
export function flashWheelDelta(deltaY: number, deltaMode: number): number {
  // Notches: DOM_DELTA_PIXEL ~100 px per notch (Chrome/Edge/Safari), DOM_DELTA_LINE 3 lines, PAGE 1.
  const notches = deltaMode === 1 ? deltaY / 3 : deltaMode === 2 ? deltaY : deltaY / 100;
  return -notches * 3;
}

/** Minimal player surface used by the adapter (FlashPlayer satisfies it). */
export interface InputPlayer {
  injectMouse(type: any, x: number, y: number, delta?: number): void;
  injectKey(type: 'down' | 'up', keyCode: number, charCode?: number, mods?: { shift?: boolean; ctrl?: boolean; alt?: boolean }): void;
  injectText?(text: string): void;
  getCursor?(): string;
  readonly stage: any;
}

export interface BrowserInputOptions {
  /** Client (CSS px) -> stage coordinates. */
  toStage: (clientX: number, clientY: number) => [number, number];
  /** Keyboard event source (default: the element's window). */
  keyTarget?: EventTarget | null;
  /** Called on every user gesture (pointer down, a touch's pointer up, key down): unlock audio etc. */
  onUserGesture?: (() => void) | null;
  /** Use a hidden <input> for text entry into focused input TextFields (default true). */
  textInput?: boolean;
  /** Called after each forwarded event (hosts may request a redraw). */
  onInput?: (() => void) | null;
  /** How long a finger's hover lasts after it is lifted (ms, default 0: MOUSE_LEAVE at once). */
  touchHoverMs?: number;
  /**
   * PORT (touch): a finger that hits no button presses the nearest one within this many CSS px when no other one is
   * about as near (TOUCH_TIE_PX); a text field or a small element of its own under the finger stays exact
   * (touch-assist.ts snapTouch). Default 0: off.
   */
  touchSlopPx?: number;
  /**
   * PORT (touch): a finger held still this long (ms) shows the hover only: its release sends 'cancel' (MOUSE_UP, no
   * CLICK) and the hover stays until the next touch, as does the hover of a tap on something that is not a button.
   * Default 0: off (the hover of every tap lasts touchHoverMs).
   */
  longPressMs?: number;
  /** PORT (touch): whether the touch aids apply to a finger that hit `target` at a stage point (default: everywhere). */
  touchAssistAt?: ((target: any, stageX: number, stageY: number) => boolean) | null;
  /**
   * PORT (touch): a finger's tap on `target` (at a stage point) that waits for the player's confirmation (a button whose
   * click cannot be undone): returning true holds the tap, whose release then does not click ('cancel'); the caller
   * asks the player and presses the button itself on yes. Fingers only; the mouse and a pen click at once.
   */
  holdTap?: ((target: any, stageX: number, stageY: number) => boolean) | null;
}

/** PORT (touch): two buttons whose distances from the finger differ by less than this (CSS px) are a tie: none is pressed. */
export const TOUCH_TIE_PX = 4;
/** PORT (touch): a non-button under the finger this small (CSS px, both sides) is a target of its own: no slop. */
export const TOUCH_OWN_TARGET_PX = 48;

/**
 * When an input event happened (ms): its timeStamp (the platform's input time, so a busy main thread that handles a
 * quick tap's press and release late does not turn the tap into a long press), else the current time.
 */
function eventTime(e: { timeStamp?: number }): number {
  return typeof e.timeStamp === 'number' && e.timeStamp > 0 ? e.timeStamp : Date.now();
}

/** A finger on the canvas: where it pressed (client and stage), when, and what the touch aids made of it. */
interface TouchPress {
  cx: number;
  cy: number;
  x: number;
  y: number;
  t: number;
  /** The touch aids apply (a finger, aids on, not where taps stay exact). */
  assisted: boolean;
  /** The press was moved onto a button near the finger. */
  snapped: boolean;
  /** What it pressed is a button (a hand-cursor object). */
  clickable: boolean;
  /** The finger has moved TAP_MOVE_PX or more: a drag, not a tap or a long press. */
  moved: boolean;
  /** What it pressed (the player's pick at the press point, after the touch aids). */
  target: any;
}

/** A second tap this close (CSS px) to the first one within the double-click time repeats its point. */
export const TOUCH_SLOP_PX = 24;
/** A tap that moved less than this (CSS px) releases at its press point. */
const TAP_MOVE_PX = 10;
/** Time between two taps of a double tap (ms; Flash's double-click time is 500 ms). */
const DOUBLE_TAP_MS = 500;

/**
 * Whether a second tap that pressed `second` repeats the first one, which pressed `first`: only an object that takes
 * double clicks (doubleClickEnabled: Flash dispatches DOUBLE_CLICK to it alone), and only when the second finger found
 * that object again, nothing interactive, or a container of it (the panel around a small button); a neighbouring
 * control is a tap of its own.
 */
function doubleTapOf(first: any, second: any): boolean {
  if (!first || !first.$doubleClickEnabled) return false;
  for (let o = first; o; o = o.$parent) if (o === second) return true;
  return second == null;
}

const KEEP_DEFAULT = new Set(['F5', 'F11', 'F12']);

/** Pixel wheel deltas of at least this size (CSS px) are treated as discrete mouse-wheel notches. */
const WHEEL_NOTCH_MIN_PX = 50;

export class BrowserInput {
  private readonly player: InputPlayer;
  private readonly el: HTMLElement;
  private readonly opts: BrowserInputOptions;
  private readonly listeners: Array<[EventTarget, string, (e: any) => void, any]> = [];
  private readonly held = new Map<string, number>();
  private pressedPointer: number | null = null;
  private wheelAcc = 0;
  private cursor = '';
  private hidden: HTMLInputElement | null = null;
  /** The hidden input's enterkeyhint as set last ('go' when it is created). */
  private enterHint = 'go';
  /** The hidden input's inputmode as set last ('' = none: the default keyboard; TextField.$inputMode 'email'). */
  private inputMode = '';
  private composing = false;
  /** Characters of the running IME composition shown in the Flash field. */
  private compLen = 0;
  /** The last touch press: client point, time, the stage point it was sent at and the object it pressed. */
  private lastTouch: { cx: number; cy: number; t: number; x: number; y: number; target: any } | null = null;
  private touchHoverTimer: ReturnType<typeof setTimeout> | null = null;
  private press: TouchPress | null = null;
  /** A finger's press the browser cancelled while the finger stayed down: still pressed in the player (onPointerCancel). */
  private interrupted: TouchPress | null = null;
  /** The release of an interrupted press after its finger's move, sent after the player's next frame (afterFrame). */
  private pendingRelease: (() => void) | null = null;
  private attached = false;

  constructor(player: InputPlayer, element: HTMLElement, opts: BrowserInputOptions) {
    this.player = player;
    this.el = element;
    this.opts = opts;
  }

  private on(target: EventTarget, type: string, fn: (e: any) => void, opts?: any): void {
    target.addEventListener(type, fn, opts);
    this.listeners.push([target, type, fn, opts]);
  }

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    const el = this.el;
    if (el.tabIndex < 0) el.tabIndex = 0;
    (el.style as any).touchAction = 'none';
    (el.style as any).outline = 'none';
    this.on(el, 'pointerdown', (e: PointerEvent) => this.onPointerDown(e));
    this.on(el, 'pointermove', (e: PointerEvent) => this.onPointerMove(e));
    this.on(el, 'pointerup', (e: PointerEvent) => this.onPointerUp(e));
    this.on(el, 'pointercancel', (e: PointerEvent) => this.onPointerCancel(e));
    this.on(el, 'pointerleave', (e: PointerEvent) => this.onPointerLeave(e));
    this.on(el, 'wheel', (e: WheelEvent) => this.onWheel(e), { passive: false });
    this.on(el, 'contextmenu', (e: Event) => e.preventDefault());
    const win: any = (el.ownerDocument && el.ownerDocument.defaultView) || globalThis;
    // The lift of a finger whose touch the browser cancelled: a pointer never pressed, over whatever it is now. Any
    // pointer pressed anywhere (the page's own controls too) ends such a press first: its lift is no such pointer.
    if (win && win.addEventListener) {
      this.on(win, 'pointerdown', () => this.endInterrupted(), true);
      this.on(win, 'pointerup', (e: PointerEvent) => this.onInterruptedLift(e), true);
    }
    const keyTarget = this.opts.keyTarget ?? win;
    if (keyTarget) {
      this.on(keyTarget, 'keydown', (e: KeyboardEvent) => this.onKey(e, 'down'));
      this.on(keyTarget, 'keyup', (e: KeyboardEvent) => this.onKey(e, 'up'));
    }
    if (win && win.addEventListener)
      this.on(win, 'blur', () => {
        this.releaseAllKeys();
        this.endInterrupted();
      });
    if (this.opts.textInput !== false && el.ownerDocument) this.createHiddenInput();
  }

  detach(): void {
    this.endInterrupted();
    this.cancelTouchHover();
    this.press = null;
    for (const [t, type, fn, o] of this.listeners) t.removeEventListener(type, fn, o);
    this.listeners.length = 0;
    if (this.hidden && this.hidden.parentNode) this.hidden.parentNode.removeChild(this.hidden);
    this.hidden = null;
    this.attached = false;
  }

  private stagePoint(e: { clientX: number; clientY: number }): [number, number] {
    return this.opts.toStage(e.clientX, e.clientY);
  }

  private gesture(): void {
    if (this.opts.onUserGesture) this.opts.onUserGesture();
  }

  private after(): void {
    this.syncTextFocus();
    this.updateCursor();
    if (this.opts.onInput) this.opts.onInput();
  }

  private onPointerDown(e: PointerEvent): void {
    this.gesture();
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    if (this.pressedPointer !== null && this.pressedPointer !== e.pointerId) return;
    // A press the browser cancelled is over by now (its lift, if it came, already sent its move).
    this.endInterrupted();
    e.preventDefault();
    try {
      this.el.focus({ preventScroll: true } as any);
    } catch {
      /* ignore */
    }
    this.pressedPointer = e.pointerId;
    try {
      this.el.setPointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    let [x, y] = this.stagePoint(e);
    this.press = null;
    if (e.pointerType !== 'mouse') {
      this.cancelTouchHover();
      const now = Date.now();
      const last = this.lastTouch;
      let target = this.pickAt(x, y);
      const repeat = !!last && now - last.t <= DOUBLE_TAP_MS && Math.hypot(e.clientX - last.cx, e.clientY - last.cy) <= TOUCH_SLOP_PX && doubleTapOf(last.target, target);
      if (repeat && last) {
        [x, y] = [last.x, last.y];
        target = last.target;
      }
      // The second tap of a double tap repeats the first one's (already snapped) point.
      const press = (this.press = this.touchPress(e, x, y, !repeat));
      if (press.snapped) target = this.pickAt(press.x, press.y);
      press.target = target;
      [x, y] = [press.x, press.y];
      this.lastTouch = { cx: e.clientX, cy: e.clientY, t: now, x, y, target };
      this.player.injectMouse('move', x, y);
    }
    const focused = this.focusedInputField();
    this.player.injectMouse('down', x, y);
    // A press on the field that already has the focus: reopen an on-screen keyboard the user dismissed.
    if (focused && focused === this.focusedInputField() && e.pointerType !== 'mouse') this.refocusText();
    this.after();
  }

  private onPointerMove(e: PointerEvent): void {
    if (this.pressedPointer !== null && e.pointerId !== this.pressedPointer) return;
    let [x, y] = this.stagePoint(e);
    const p = this.press;
    if (p && e.pointerId === this.pressedPointer) {
      if (!p.moved && Math.hypot(e.clientX - p.cx, e.clientY - p.cy) >= TAP_MOVE_PX) p.moved = true;
      // A press moved onto a button stays on it while the finger only trembles.
      if (p.snapped && !p.moved) [x, y] = [p.x, p.y];
    }
    this.player.injectMouse('move', x, y);
    this.after();
  }

  /** The touch aids for a finger pressing at stage point (x, y): where it presses and how its release is treated. */
  private touchPress(e: PointerEvent, x: number, y: number, snap: boolean): TouchPress {
    const p: TouchPress = { cx: e.clientX, cy: e.clientY, x, y, t: eventTime(e), assisted: false, snapped: false, clickable: true, moved: false, target: null };
    const slop = this.opts.touchSlopPx ?? 0;
    const stage = this.player.stage;
    if (e.pointerType !== 'touch' || (!(slop > 0) && !((this.opts.longPressMs ?? 0) > 0))) return p;
    if (!stage || typeof stage.$mousePick !== 'function') return p;
    let target = pickAt(stage, x, y);
    if (this.opts.touchAssistAt && !this.opts.touchAssistAt(target, x, y)) return p;
    p.assisted = true;
    if (snap && slop > 0) {
      // Stage px per CSS px here (the view may be zoomed): the slop and the tie are finger distances on the screen.
      const [ax, ay] = this.opts.toStage(e.clientX, e.clientY);
      const [bx, by] = this.opts.toStage(e.clientX + 100, e.clientY);
      const k = Math.hypot(bx - ax, by - ay) / 100 || 1;
      const s = snapTouch(stage, x, y, { radius: slop * k, tie: TOUCH_TIE_PX * k, ownTarget: TOUCH_OWN_TARGET_PX * k });
      if (s) {
        p.x = s.x;
        p.y = s.y;
        p.snapped = true;
        target = s.target;
      }
    }
    p.clickable = !!clickableOf(target);
    return p;
  }

  private onPointerUp(e: PointerEvent): void {
    // A finger's pointerup is the user activation of a touch (its pointerdown is not): audio unlocks here at the first tap.
    if (e.type === 'pointerup' && e.pointerType !== 'mouse') this.gesture();
    if (this.pressedPointer === null || e.pointerId !== this.pressedPointer) return;
    if (e.pointerType === 'mouse' && e.button !== 0 && e.type === 'pointerup') return;
    this.pressedPointer = null;
    try {
      this.el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    let [x, y] = this.stagePoint(e);
    const last = this.lastTouch;
    if (e.pointerType !== 'mouse' && last && Math.hypot(e.clientX - last.cx, e.clientY - last.cy) < TAP_MOVE_PX) [x, y] = [last.x, last.y];
    const p = this.press;
    this.press = null;
    this.release(e, p, x, y)();
    this.after();
  }

  /**
   * The press of `p` (null: a mouse) ends at stage point (x, y) with the event `e` that ended it: returns what sends the
   * release (a tap or a drag: 'up'; a long press or a tap that waits for the player's confirmation: 'cancel') and
   * starts the finger's hover time.
   */
  private release(e: PointerEvent, p: TouchPress | null, x: number, y: number): () => void {
    const longMs = this.opts.longPressMs ?? 0;
    const still = !!p && !p.moved && Math.hypot(e.clientX - p.cx, e.clientY - p.cy) < TAP_MOVE_PX;
    // A long press shows the hover (tooltips, a button's over state) and does not click.
    const long = !!p && p.assisted && longMs > 0 && still && eventTime(e) - p.t >= longMs;
    // A tap that waits for the player's confirmation (holdTap): released without its click.
    const held = !long && !!p && e.type === 'pointerup' && e.pointerType === 'touch' && !!p.target && !!this.opts.holdTap && this.pickAt(x, y) === p.target && this.opts.holdTap(p.target, x, y);
    // After a long press, or a tap on something that is not a button (its tooltip is all a tap gives), the hover stays
    // until the next touch (the next tap acts as usual); otherwise it lasts touchHoverMs.
    const sticky = !!p && p.assisted && longMs > 0 && (long || !p.clickable);
    const touch = e.pointerType !== 'mouse';
    return () => {
      this.player.injectMouse(long || held ? 'cancel' : 'up', x, y);
      if (touch && !sticky) {
        const ms = this.opts.touchHoverMs ?? 0;
        if (ms > 0) {
          this.touchHoverTimer = setTimeout(() => {
            this.touchHoverTimer = null;
            this.player.injectMouse('leave', x, y);
            this.after();
          }, ms);
        } else this.player.injectMouse('leave', x, y);
      }
    };
  }

  /**
   * A finger's press cancelled by the browser. Android's WebView cancels a touch whose start waited about a second for
   * a busy page (a slow device drawing a frame): pointercancel at once after its pointerdown, no moves, and the finger's
   * lift later as a pointerup of a pointer never pressed (onInterruptedLift). The finger is still down: its press stays
   * in the player, neither released nor clicked, until that lift. A mouse's or a pen's cancel releases as before.
   */
  private onPointerCancel(e: PointerEvent): void {
    if (e.pointerType !== 'touch' || this.pressedPointer === null || e.pointerId !== this.pressedPointer || !this.press) {
      this.onPointerUp(e);
      return;
    }
    this.pressedPointer = null;
    try {
      this.el.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    this.interrupted = this.press;
    this.press = null;
    this.after();
  }

  /**
   * The lift of a finger whose press the browser cancelled (onPointerCancel): a touch pointer's pointerup that no press
   * on the canvas holds, wherever it is now. The player gets the finger's way at once (a move to where it lifted: a
   * drag of the battle camera moves it, as the game reads the mouse in its frames) and its release after its next
   * frame (afterFrame), as the pointerup of that press would have released it.
   */
  private onInterruptedLift(e: PointerEvent): void {
    const p = this.interrupted;
    if (!p || e.pointerType !== 'touch' || e.pointerId === this.pressedPointer) return;
    this.interrupted = null;
    // (A pointerup on the canvas reports its user activation there: onPointerUp.)
    if (e.target !== this.el) this.gesture();
    let [x, y] = this.stagePoint(e);
    const moved = Math.hypot(e.clientX - p.cx, e.clientY - p.cy) >= TAP_MOVE_PX;
    if (moved) p.moved = true;
    else [x, y] = [p.x, p.y];
    this.player.injectMouse('move', x, y);
    this.flushRelease();
    this.pendingRelease = this.release(e, p, x, y);
    this.after();
  }

  /** Sends the pending release of an interrupted press now. */
  private flushRelease(): void {
    const r = this.pendingRelease;
    this.pendingRelease = null;
    if (r) r();
  }

  /** Ends an interrupted press now: its pending release, or (a finger whose lift never came) a release without a click. */
  private endInterrupted(): void {
    this.flushRelease();
    const p = this.interrupted;
    if (!p) return;
    this.interrupted = null;
    this.player.injectMouse('cancel', p.x, p.y);
    this.player.injectMouse('leave', p.x, p.y);
  }

  /**
   * Hosts call this after every frame of the player's loop with the number of its ticks: the release of an interrupted
   * press waits for a frame that ticked after the finger's move (the game reads the pressed mouse in its frames).
   */
  afterFrame(ticks: number): void {
    if (!this.pendingRelease || !(ticks > 0)) return;
    this.flushRelease();
    this.after();
  }

  /** The interactive object a press at this stage point reaches (the player's own mouse pick), or null. */
  private pickAt(x: number, y: number): any {
    try {
      return pickAt(this.player.stage, x, y);
    } catch {
      return null;
    }
  }

  private cancelTouchHover(): void {
    if (this.touchHoverTimer) clearTimeout(this.touchHoverTimer);
    this.touchHoverTimer = null;
  }

  private onPointerLeave(e: PointerEvent): void {
    if (this.pressedPointer !== null) return; // captured: keep tracking
    if (e.pointerType !== 'mouse') return;
    const [x, y] = this.stagePoint(e);
    this.player.injectMouse('leave', x, y);
    this.after();
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const step = flashWheelDelta(e.deltaY, e.deltaMode);
    if (step === 0) return;
    let d: number;
    if (e.deltaMode !== 0 || Math.abs(e.deltaY) >= WHEEL_NOTCH_MIN_PX) {
      // A discrete wheel notch (Chrome/Edge: 100 CSS px at 100% zoom, 50 px at 200% zoom or with
      // an emulated devicePixelRatio of 2, ~53 px on some Linux setups; Firefox: 3 lines): always
      // whole Flash notches of 3, never a fraction.
      d = Math.sign(step) * 3 * Math.max(1, Math.round(Math.abs(step) / 3));
      this.wheelAcc = 0;
    } else {
      // Touchpads: small pixel deltas accumulate (100 px = one notch). A reversed direction
      // discards the residue of the previous one.
      if (step * this.wheelAcc < 0) this.wheelAcc = 0;
      this.wheelAcc += step;
      d = this.wheelAcc >= 0 ? Math.floor(this.wheelAcc) : Math.ceil(this.wheelAcc);
      if (d === 0) return;
      this.wheelAcc -= d;
    }
    const [x, y] = this.stagePoint(e);
    this.player.injectMouse('wheel', x, y, d);
    this.after();
  }

  private isForeignEditable(target: any): boolean {
    if (!target || target === this.hidden) return false;
    const tag = (target.tagName || '').toUpperCase();
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || !!target.isContentEditable;
  }

  private onKey(e: KeyboardEvent, type: 'down' | 'up'): void {
    if (this.isForeignEditable(e.target)) return;
    if (type === 'down') this.gesture();
    const keyCode = flashKeyCode(e);
    if (keyCode === 229 || e.isComposing) return; // IME: text arrives through the hidden input
    const altGr = typeof e.getModifierState === 'function' && e.getModifierState('AltGraph');
    const mods = { shift: e.shiftKey, ctrl: (e.ctrlKey || e.metaKey) && !altGr, alt: e.altKey && !altGr };
    const charCode = flashCharCode(e);
    const id = e.code || String(keyCode);
    if (type === 'down') this.held.set(id, keyCode);
    else this.held.delete(id);
    const shortcut = e.ctrlKey || e.metaKey;
    if (!shortcut && !KEEP_DEFAULT.has(e.code)) e.preventDefault();
    if (keyCode) this.player.injectKey(type, keyCode, charCode, mods);
    this.after();
  }

  /** Release every key still held (window blur): prevents stuck movement keys. */
  releaseAllKeys(): void {
    for (const keyCode of this.held.values()) this.player.injectKey('up', keyCode, 0, {});
    this.held.clear();
  }

  // ------------------------------------------------------------------ text input

  private createHiddenInput(): void {
    const doc = this.el.ownerDocument;
    const inp = doc.createElement('input');
    inp.type = 'text';
    inp.setAttribute('autocomplete', 'off');
    inp.setAttribute('autocorrect', 'off');
    inp.setAttribute('autocapitalize', 'off');
    inp.setAttribute('spellcheck', 'false');
    inp.setAttribute('enterkeyhint', 'go');
    inp.setAttribute('aria-hidden', 'true');
    inp.tabIndex = -1;
    const s = inp.style as any;
    s.position = 'fixed';
    s.left = '0px';
    s.top = '0px';
    s.width = '1px';
    s.height = '1px';
    s.opacity = '0';
    s.border = '0';
    s.padding = '0';
    s.fontSize = '16px'; // no iOS zoom on focus
    s.pointerEvents = 'none';
    s.zIndex = '-1';
    (doc.body || doc.documentElement).appendChild(inp);
    this.on(inp, 'compositionstart', () => {
      this.composing = true;
      this.compLen = 0;
    });
    // The word being composed is shown in the field as it is typed, then replaced by the committed text.
    this.on(inp, 'compositionupdate', (e: CompositionEvent) => {
      this.showComposition(e.data ?? '');
      this.after();
    });
    this.on(inp, 'compositionend', (e: CompositionEvent) => {
      this.showComposition(e.data ?? '');
      this.composing = false;
      this.compLen = 0;
      inp.value = '';
      this.after();
    });
    this.on(inp, 'input', (e: InputEvent) => {
      // Composed text is applied by the composition events (Firefox repeats it in an input event after compositionend).
      if (this.composing || e.isComposing || e.inputType === 'insertCompositionText') {
        if (!this.composing) inp.value = '';
        return;
      }
      const t = e.inputType || '';
      if (t === 'deleteContentBackward') {
        this.player.injectKey('down', 8, 8, {});
        this.player.injectKey('up', 8, 8, {});
      } else if (t === 'deleteContentForward') {
        this.player.injectKey('down', 46, 127, {});
        this.player.injectKey('up', 46, 127, {});
      } else if (e.data) this.commitText(e.data);
      else if (inp.value) this.commitText(inp.value);
      inp.value = '';
      this.after();
    });
    this.on(inp, 'paste', (e: ClipboardEvent) => {
      const text = e.clipboardData ? e.clipboardData.getData('text') : '';
      e.preventDefault();
      if (text) this.commitText(text);
      this.after();
    });
    this.hidden = inp;
  }

  private commitText(text: string): void {
    if (this.player.injectText) this.player.injectText(text);
  }

  /** Replaces the composition shown in the focused field (its last `compLen` characters before the caret) by `text`. */
  private showComposition(text: string): void {
    const field = this.focusedInputField();
    if (!field) {
      this.compLen = 0;
      return;
    }
    const length = () => String(field.text ?? '').length;
    const base = length() - this.compLen;
    for (let i = 0; i < this.compLen; i++) {
      this.player.injectKey('down', 8, 8, {});
      this.player.injectKey('up', 8, 8, {});
    }
    if (text) this.commitText(text);
    // What the field kept of it (restrict / maxChars may drop characters).
    this.compLen = Math.max(0, length() - base);
  }

  /** Blur and focus the hidden input inside a user gesture: the on-screen keyboard opens again. */
  private refocusText(): void {
    const inp = this.hidden;
    if (!inp || inp.ownerDocument?.activeElement !== inp) return;
    try {
      inp.blur();
      inp.focus({ preventScroll: true } as any);
    } catch {
      /* ignore */
    }
  }

  /** The Flash input TextField that has stage focus, if any. */
  private focusedInputField(): any {
    const st = this.player.stage;
    const f = st && (st.$focus ?? st.focus);
    return f && f.$renderKind === 'text' && f.$type === 'input' && f.stage === st ? f : null;
  }

  /**
   * Move DOM focus to the hidden input while a Flash input field is focused (and back to the
   * canvas afterwards). Hosts call this once per frame (game code may change stage.focus).
   */
  syncTextFocus(): void {
    const inp = this.hidden;
    if (!inp) return;
    const doc = this.el.ownerDocument;
    const field = this.focusedInputField();
    const want = !!field;
    const has = doc.activeElement === inp;
    // A password field (displayAsPassword, also while a "show" toggle shows its text: $sensitive) gets a password
    // <input>: virtual keyboards then offer no suggestions and learn nothing from what is typed.
    const kind = field && (field.displayAsPassword || field.$sensitive) ? 'password' : 'text';
    if (inp.type !== kind) {
      try {
        inp.type = kind;
        inp.value = '';
      } catch {
        /* ignore */
      }
    }
    // The keyboard's layout as the field asks for it (an e-mail field: the keyboard with "@"); text otherwise.
    const mode = (field && typeof field.$inputMode === 'string' && field.$inputMode) || '';
    if (mode !== this.inputMode) {
      this.inputMode = mode;
      try {
        if (mode) inp.setAttribute('inputmode', mode);
        else inp.removeAttribute('inputmode');
      } catch {
        /* ignore */
      }
    }
    // The keyboard's Enter key as the field asks for it ("next" in a form's first field, else "go").
    const hint = (field && typeof field.$enterKeyHint === 'string' && field.$enterKeyHint) || 'go';
    if (hint !== this.enterHint) {
      this.enterHint = hint;
      try {
        inp.setAttribute('enterkeyhint', hint);
      } catch {
        /* ignore */
      }
    }
    // A modal dialog of the page (aria-modal="true", e.g. src/client/platform/dialogs.ts) owns the focus while one of
    // its controls has it: taking it back every frame would send the keys typed there into the game's field.
    const active = doc.activeElement as Element | null;
    const modal = !has && !!active && typeof active.closest === 'function' && active.closest('[aria-modal="true"]') !== null;
    if (want && !has && !modal) {
      try {
        inp.value = '';
        inp.focus({ preventScroll: true } as any);
      } catch {
        /* ignore */
      }
    } else if (!want && has) {
      try {
        this.el.focus({ preventScroll: true } as any);
      } catch {
        /* ignore */
      }
    }
  }

  /** Apply the Flash cursor (hand/I-beam/none/default) to the element. */
  updateCursor(): void {
    const c = this.player.getCursor ? this.player.getCursor() : 'default';
    const css = c === 'none' ? 'none' : c === 'pointer' ? 'pointer' : c === 'text' ? 'text' : 'default';
    if (css !== this.cursor) {
      this.cursor = css;
      this.el.style.cursor = css;
    }
  }
}
